import { forwardRef, useImperativeHandle, useState } from "react";
import { GALLERY_GOALS, GalleryGoalOption } from "./galleryGoals";
import { useIntentSelection } from "../intentOptions";
import { LogInPrompt } from "./LogInPrompt";
import {
  ANNUAL_SALES,
  CAREER_STAGES,
  CareerStageCode,
  DEFAULT_SALES_BY_STAGE,
  RevenueBandCode,
  validateName,
  validateNameLength,
} from "../profileOptions";

// ACA-266 AC3/AC4: step 2 of the rebuilt wizard, parametrized (ACA-267 Phase 3) so the same
// component serves three personas - gallery, artist and Collector, since ACA-268. Every prop
// defaults to the gallery's own values, which is what keeps this file's existing tests passing
// unchanged - the gallery still asks one thing, what it wants to do next, and nothing else.
//
// The gallery used to carry a "Your organisation" section too (Gallery Name, Gallery Website,
// Career stage, Annual sales), which is what the signed design draws. That section was removed for
// the gallery on 24/09 by the product decision recorded in Slack: Gallery Name and Gallery Website
// moved to the account screen, so that screen asks for exactly what today's /register asks for, and
// Career stage and Annual sales are no longer collected during the gallery's signup at all. The
// artist still collects all four here, behind `practiceFields`. The Collector, since 26/09
// (D1), collects the name/website pair only - `careerFields` is what draws that line.
//
// Nothing was dropped from the database for the gallery. `User.CareerStage` and
// `User.AnnualSalesBracket` still exist and still hold whatever a record already had:
// OnboardingController.PostSettingsAsync writes each field behind its own guard
// (`if (careerStage.HasValue)`), so a PUT that omits them leaves them alone rather than blanking
// them.
//
// The "Skip for now" exit went the same way on 25/09, by the same kind of product decision. The
// goals are the only thing the gallery step asks and they are now a required answer: Continue is
// the one way out, and it holds the step until at least one goal is picked. The artist's Continue
// also holds on a blank Career stage - see the note above handleContinue.
//
// This is a leaf: it takes copy and behaviour through props rather than importing personaCopy.ts
// itself. personaCopy.ts imports GetStartedStep.tsx, a screen component - if this step read the
// table directly, it would drag that screen into this file's own test suite.

export interface GalleryPracticeDetails {
  /** The goals as a SignupIntent bitmask. Null is the container's own value for a rehydrated
      record carrying no intent (OnboardingV2.tsx); this step never hands one back now that
      Continue - which holds the step until a goal is picked - is the only way out. */
  intent: number | null;
  /**
   * Labels of the picked goals, in click order: a click appends, an unclick removes, and a
   * reclick moves the label back to the end rather than restoring its old position. This is what
   * lets the Account step's confirmation sentence (AC4) name the goal picked first - the bitmask
   * alone cannot, since it has no order.
   */
  pickedLabels: string[];
  /**
   * The fields `practiceFields` renders: studio/gallery name and website, plus career stage and
   * annual sales when `careerFields` is on too. Optional, and only ever populated for a persona
   * whose flags ask for them - OnboardingV2.tsx builds this object without any of them.
   */
  studioName?: string;
  website?: string;
  careerStage?: CareerStageCode;
  annualSales?: RevenueBandCode;
}

export interface GalleryPracticeStepProps {
  isArtistReferral?: boolean;
  /** What a container already holds - answers from earlier in this session, or a rehydrated
      ghost's. Read once, at mount - not a controlled value, same pattern as GalleryAccountStep. */
  initialValues?: Partial<GalleryPracticeDetails>;
  /** The container's last network rejection (registrations or settings), shown as the server
      wrote it - same contract as GalleryAccountStep's `error`. */
  error?: string;
  loading?: boolean;
  /**
   * Back to the picker. The label is the persona, the way the design writes it. Carries whatever
   * is currently in the fields, unvalidated - same contract as GalleryAccountStep's onBack, and
   * for the same reason: a 1 -> 2 -> 1 round trip (D5) must not throw typed answers away.
   *
   * Optional, unlike GalleryAccountStep's onBack: once a session exists, the container has
   * nowhere for this to go (ACA-266 fix, "step 1 is not a navigation target once a session
   * exists") and omits it rather than hand over a callback that would swap a live identity's
   * persona. Absent, the chevron itself does not render - the same optional-affordance shape
   * OnboardingV2Stage already uses for a tracker step with no onSelect.
   */
  onBack?: (details: GalleryPracticeDetails) => void;
  onContinue: (details: GalleryPracticeDetails) => void;
  /** The goals offered as toggles. Defaults to GALLERY_GOALS. */
  goals?: readonly GalleryGoalOption[];
  /** The screen's own heading, above the goals. The lead paragraph beneath it does not vary by
      persona and stays a literal below. */
  heading?: string;
  /** Label beside the Back chevron - the persona's own name, the way the design writes it. */
  backLabel?: string;
  /**
   * Prefix for each goal's `data-testid` (`${prefix}-${goal.value}`). GALLERY_GOALS and
   * ARTIST_GOALS share bits (2, 4 and 128), so a shared prefix would let one persona's testid
   * match the other persona's chip too. e2e/tests/auth/register.spec.ts already targets
   * `gallery-goal-2`, which is why the default has to stay "gallery-goal".
   */
  goalTestIdPrefix?: string;
  /**
   * Renders the organisation section below the goals - studio/gallery name, website, and, behind
   * `careerFields`, career stage and annual sales. False for the gallery: those fields moved to
   * the account step on 24/09 (see the file header) and career stage/annual sales are not asked
   * during the gallery's signup at all. True for the artist and the Collector.
   */
  practiceFields?: boolean;
  /** Read only when `practiceFields` is on. Defaults true - the shape the section has always had. */
  careerFields?: boolean;
  orgRequired?: boolean;
  validateOrgNameFormat?: boolean;
  /** The organisation section's own `<h2>`. Only read when `practiceFields` is true. */
  sectionLabel?: string;
  orgLabel?: string;
  orgPlaceholder?: string;
  siteLabel?: string;
  sitePlaceholder?: string;
}

/**
 * What the tracker's own step links need and a plain onBack prop cannot give them: they live in
 * OnboardingV2Stage, outside this component's tree, and do not go through the Back button's click
 * handler - so a container that wants the same capture on a tracker-driven 2 -> 1 (D5) reads it
 * through this handle instead.
 */
export interface GalleryPracticeStepHandle {
  captureDetails: () => GalleryPracticeDetails;
}

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const GalleryPracticeStep = forwardRef<
  GalleryPracticeStepHandle,
  GalleryPracticeStepProps
>(function GalleryPracticeStep(
  {
    isArtistReferral,
    initialValues,
    error,
    loading,
    onBack,
    onContinue,
    goals = GALLERY_GOALS,
    heading = "Tell us about your business.",
    backLabel = "Gallery",
    goalTestIdPrefix = "gallery-goal",
    practiceFields = false,
    careerFields = true,
    orgRequired = true,
    validateOrgNameFormat = true,
    sectionLabel,
    orgLabel = "Gallery Name",
    orgPlaceholder = "Gallery or business name",
    siteLabel = "Gallery Website",
    sitePlaceholder = "yourgallery.com",
  },
  ref,
) {
  // The bitmask and the labels have to come from the SAME source, or bit 128 splits them apart: it
  // is the one bit the two goal tables share under different labels ("Handle consignments" here,
  // "Consign to galleries" for the artist), so a label carried over from the other table's seed
  // would neither match this table's own text nor deserve to be dropped - the bit is legitimate
  // here too. Matched labels keep the order they arrived in; a bit with no matching label (because
  // the incoming text belongs to the other table) is reconstructed from this table instead of lost.
  const goalMask = goals.reduce((mask, goal) => mask | goal.value, 0);
  const seededIntent = (initialValues?.intent ?? 0) & goalMask;
  const seededLabels = (initialValues?.pickedLabels ?? []).filter((label) =>
    goals.some((goal) => goal.label === label && (seededIntent & goal.value) !== 0),
  );
  const seededBits = seededLabels.reduce(
    (mask, label) => mask | (goals.find((goal) => goal.label === label)?.value ?? 0),
    0,
  );
  const [pickedLabels, setPickedLabels] = useState<string[]>([
    ...seededLabels,
    ...goals
      .filter((goal) => (seededIntent & goal.value) !== 0 && (seededBits & goal.value) === 0)
      .map((goal) => goal.label),
  ]);
  const [studioName, setStudioName] = useState(initialValues?.studioName ?? "");
  const [website, setWebsite] = useState(initialValues?.website ?? "");
  const [careerStage, setCareerStage] = useState<CareerStageCode | undefined>(
    initialValues?.careerStage,
  );
  const [annualSales, setAnnualSales] = useState<RevenueBandCode | undefined>(
    initialValues?.annualSales,
  );
  // studioName holds the message to show, "" meaning no error - validateName's message varies
  // by failure reason, unlike goals/stage below.
  const [errors, setErrors] = useState<{ goals: boolean; stage: boolean; studioName: string }>({
    goals: false,
    stage: false,
    studioName: "",
  });

  // A field only ever rides in `details` when the flag that renders it is on: the gallery's own
  // callers (OnboardingV2.test.tsx) match the payload with toHaveBeenCalledWith, which fails on
  // any extra key.
  const details = (intent: number | null): GalleryPracticeDetails => {
    const base: GalleryPracticeDetails = { intent, pickedLabels };
    if (!practiceFields) return base;
    const organisation: GalleryPracticeDetails = {
      ...base,
      studioName: studioName.trim(),
      website: website.trim(),
    };
    if (!careerFields) return organisation;
    return { ...organisation, careerStage, annualSales };
  };

  const { intent, toggle, submit } = useIntentSelection({
    options: [...goals],
    isArtistReferral,
    initialIntent: seededIntent || undefined,
    onContinue: (mask) => onContinue(details(mask)),
  });

  useImperativeHandle(ref, () => ({ captureDetails: () => details(intent) }));

  // The checks run before submit(), never inside it: submit() is what emits
  // onboarding_submitted, and a beacon that fires on a rejected step reports a funnel
  // step nobody completed. It is also why the hook's own `error` goes unread here.
  // The Career stage and Studio Name checks restored below only apply when `practiceFields` is
  // on, so the gallery (practiceFields false) is unchanged: the goals are still the only way it
  // can be incomplete.
  const handleContinue = () => {
    const trimmedStudioName = studioName.trim();
    // The blank case is handled apart from validateName's own message, the way GalleryAccountStep
    // handles Full Name's: "Required" reads as a form-generated label, not the sentence its
    // neighbours ("Select a career stage.", "Pick at least one.") already use.
    const validateOrgName = validateOrgNameFormat ? validateName : validateNameLength;
    const studioNameResult =
      practiceFields && trimmedStudioName !== "" ? validateOrgName(studioName) : undefined;
    // The blank-field sentence names the field by orgLabel rather than a persona-specific
    // literal: this section is shared (ACA-268 gave the collector practiceFields too, after only
    // the artist had ever rendered it), and "Enter your studio name." under a "Collection Name"
    // label read as the artist's copy leaking into the collector's screen - QA caught it live.
    // For the artist orgLabel is still "Studio Name", so this produces the exact string above,
    // byte for byte.
    const next = {
      goals: intent === 0,
      stage: practiceFields && careerFields && !careerStage,
      studioName: !practiceFields
        ? ""
        : orgRequired && trimmedStudioName === ""
          ? `Enter your ${orgLabel.toLowerCase()}.`
          : studioNameResult && !studioNameResult.ok
            ? (studioNameResult.msg ?? "")
            : "",
    };
    setErrors(next);
    if (next.goals || next.stage || next.studioName) return;
    submit();
  };

  // isSelected is recomputed here with the same expression the render below uses, off the same
  // `intent` snapshot - that is what keeps pickedLabels and the bitmask as two views of one
  // choice instead of two answers that could drift apart.
  const handleToggle = (goal: { value: number; label: string }) => {
    const isSelected = (intent & goal.value) !== 0;
    setPickedLabels((current) =>
      isSelected
        ? current.filter((label) => label !== goal.label)
        : [...current.filter((label) => label !== goal.label), goal.label],
    );
    toggle(goal.value);
    setErrors((current) => ({ ...current, goals: false }));
  };

  return (
    <div className="gallery-practice">
      {onBack && (
        <button
          type="button"
          className="onboarding-v2__back"
          onClick={() => onBack(details(intent))}
          // Onboarding.jsx:647's full-screen <ModalBackground /> blocked every control while
          // `loading`, this chevron included - the same rule Continue already gets below, not
          // a new one invented for this button.
          disabled={loading || undefined}
          aria-busy={loading || undefined}
          data-testid="practice-back"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m14 6-6 6 6 6" />
          </svg>
          <span>{backLabel}</span>
        </button>
      )}

      <h1 className="onboarding-v2__heading" data-testid="screen-heading">
        {heading}
      </h1>
      <p className="onboarding-v2__lead" data-testid="screen-subheading">
        Your Getting Started checklist is built around this. Nothing here is locked in
        &mdash; you can change it later.
      </p>

      {error && (
        <p className="gallery-practice__server-error" role="alert" data-testid="server-error">
          {error}
        </p>
      )}

      <h2 className="onboarding-v2__section-label" id="gallery-goals-label">
        What you want to do first
      </h2>
      <div
        className="gallery-practice__goals"
        role="group"
        aria-labelledby="gallery-goals-label"
        aria-describedby={errors.goals ? "gallery-goals-error" : undefined}
      >
        {goals.map((goal) => {
          const isSelected = (intent & goal.value) !== 0;
          return (
            <button
              key={goal.value}
              type="button"
              className="gallery-practice__goal"
              aria-pressed={isSelected}
              data-testid={`${goalTestIdPrefix}-${goal.value}`}
              onClick={() => handleToggle(goal)}
            >
              <span className="gallery-practice__goal-icon">
                {isSelected ? <CheckIcon /> : <PlusIcon />}
              </span>
              <span>{goal.label}</span>
            </button>
          );
        })}
      </div>
      {errors.goals && (
        <p
          className="gallery-practice__error"
          id="gallery-goals-error"
          role="alert"
          data-testid="gallery-goals-error"
        >
          Pick at least one.
        </p>
      )}

      {practiceFields && (
        <>
          {sectionLabel && (
            <h2 className="onboarding-v2__section-label">{sectionLabel}</h2>
          )}
          <div className="gallery-practice__fields">
            <div className="gallery-practice__field">
              <label className="gallery-practice__label" htmlFor="practice-org">
                {orgLabel}{" "}
                {/* The asterisk is the design's mark for a required field and is decoration;
                    aria-required on the control is what a screen reader announces. */}
                {orgRequired && (
                  <span className="gallery-practice__required" aria-hidden="true">
                    *
                  </span>
                )}
              </label>
              <input
                id="practice-org"
                className="gallery-practice__input"
                type="text"
                autoComplete="organization"
                placeholder={orgPlaceholder}
                value={studioName}
                aria-required={orgRequired || undefined}
                aria-invalid={Boolean(errors.studioName) || undefined}
                aria-describedby={errors.studioName ? "practice-org-error" : undefined}
                onChange={(event) => {
                  setStudioName(event.target.value);
                  setErrors((current) => ({ ...current, studioName: "" }));
                }}
                data-testid="practice-org"
              />
              {errors.studioName && (
                <p
                  className="gallery-practice__error"
                  id="practice-org-error"
                  role="alert"
                  data-testid="practice-org-error"
                >
                  {errors.studioName}
                </p>
              )}
            </div>

            <div className="gallery-practice__field">
              <label className="gallery-practice__label" htmlFor="practice-site">
                {siteLabel}
              </label>
              <input
                id="practice-site"
                className="gallery-practice__input"
                type="text"
                inputMode="url"
                autoComplete="url"
                placeholder={sitePlaceholder}
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                data-testid="practice-site"
              />
            </div>

            {careerFields && (
              <>
                <div className="gallery-practice__field">
                  <label className="gallery-practice__label" htmlFor="practice-career-stage">
                    Career stage{" "}
                    {/* The asterisk is the design's mark for a required field and is decoration;
                        aria-required on the control is what a screen reader announces. */}
                    <span className="gallery-practice__required" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <select
                    id="practice-career-stage"
                    className="gallery-practice__select"
                    aria-required="true"
                    aria-invalid={errors.stage || undefined}
                    aria-describedby={errors.stage ? "practice-career-stage-error" : undefined}
                    value={careerStage ?? ""}
                    data-testid="practice-career-stage"
                    onChange={(event) => {
                      const stage = event.target.value as CareerStageCode | "";
                      setCareerStage(stage || undefined);
                      // Mirrors OnboardingProfileScreen's handleCareerStageChange: picking a stage
                      // always overwrites Annual sales with that stage's default band, even one the
                      // visitor already chose for themselves - there is no branch here that checks
                      // for an existing value first.
                      if (stage) {
                        setAnnualSales(DEFAULT_SALES_BY_STAGE[stage]);
                      }
                      setErrors((current) => ({ ...current, stage: false }));
                    }}
                  >
                    <option value="">Select stage</option>
                    {CAREER_STAGES.map((stage) => (
                      <option key={stage.value} value={stage.value}>
                        {stage.label} &mdash; {stage.subLabel}
                      </option>
                    ))}
                  </select>
                  {errors.stage && (
                    <p
                      className="gallery-practice__error"
                      id="practice-career-stage-error"
                      role="alert"
                      data-testid="practice-career-stage-error"
                    >
                      Select a career stage.
                    </p>
                  )}
                </div>

                <div className="gallery-practice__field">
                  <label className="gallery-practice__label" htmlFor="practice-annual-sales">
                    Annual sales
                  </label>
                  <select
                    id="practice-annual-sales"
                    className="gallery-practice__select"
                    value={annualSales ?? ""}
                    data-testid="practice-annual-sales"
                    onChange={(event) =>
                      setAnnualSales((event.target.value || undefined) as RevenueBandCode | undefined)
                    }
                  >
                    <option value="">Select range</option>
                    {ANNUAL_SALES.map((band) => (
                      <option key={band.value} value={band.value}>
                        {band.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div className="gallery-practice__actions">
        <button
          type="button"
          className="onboarding-v2__cta"
          onClick={handleContinue}
          disabled={loading || undefined}
          aria-busy={loading || undefined}
          data-testid="practice-continue"
        >
          Continue
        </button>
        <LogInPrompt />
      </div>
    </div>
  );
});
